import dayjs from "dayjs";
import { type ItemState, type OnboardingDetailItem, type OnboardingHistoryEntry } from "../../types/onboarding";
import { HISTORY_ACTION_LABELS, ITEM_NETWORK_LABELS, ITEM_STATE_LABELS } from "./labels";

export type RowStatus = "todo" | "partial" | "waiting" | "done" | "not_needed";

export interface Row {
  key: string;
  title: string;
  items: OnboardingDetailItem[];
  status: RowStatus;
  label: string;
  overdue: boolean;
}

export const STATUS_CLASSES: Record<RowStatus, string> = {
  todo: "text-gray-500",
  partial: "text-brand-500",
  waiting: "text-warning-600",
  done: "text-success-600",
  not_needed: "text-gray-400",
};

export const isClosed = (status: RowStatus) => status === "done" || status === "not_needed";
export const isLive = (item: OnboardingDetailItem) => item.state !== "not_needed";

export const toDaysAgo = (days: number) => (days <= 0 ? "today" : days === 1 ? "yesterday" : `${days} days ago`);

export function toWaitedLabel(item: OnboardingDetailItem): string {
  return `${item.party === "network" ? "Sent" : "Asked"} ${toDaysAgo(item.daysWaiting ?? 0)}`;
}

export const withoutBrand = (item: OnboardingDetailItem) => {
  const suffix = ` (${item.brandName})`;
  return item.brandName !== null && item.title.endsWith(suffix) ? item.title.slice(0, -suffix.length) : item.title;
};

function toStatus(items: OnboardingDetailItem[]): Pick<Row, "status" | "label" | "overdue"> {
  const live = items.filter(isLive);
  if (!live.length) return { status: "not_needed", label: ITEM_STATE_LABELS.not_needed, overdue: false };
  if (live.every((i) => i.state === "done")) return { status: "done", label: ITEM_STATE_LABELS.done, overdue: false };
  const waiting = live.filter((i) => i.state === "waiting");
  if (waiting.length) {
    const overdue = waiting.some((i) => i.overdue);
    const longest = waiting.reduce((a, b) => ((b.daysWaiting ?? 0) > (a.daysWaiting ?? 0) ? b : a));
    return { status: "waiting", label: toWaitedLabel(longest), overdue };
  }
  if (live.some((i) => i.state === "done")) return { status: "partial", label: "In progress", overdue: false };
  return { status: "todo", label: ITEM_STATE_LABELS.todo, overdue: false };
}

// Per-brand items with the same step title show as one row with a chip per brand; the title carries
// params like the processor name, so different processors stay separate rows.
export function toRows(items: OnboardingDetailItem[]): Row[] {
  const rows = new Map<string, OnboardingDetailItem[]>();
  for (const item of items) {
    const key = item.brandName === null ? `item-${item.itemId}` : `${item.groupKey}-${item.itemKey}-${withoutBrand(item)}`;
    rows.set(key, [...(rows.get(key) ?? []), item]);
  }
  return [...rows].map(([key, group]) => ({ key, title: withoutBrand(group[0]), items: group, ...toStatus(group) }));
}

// One instruction block, or one per brand when the brands' texts differ (e.g. the refund-by-hand note).
export const toTexts = (row: Row) =>
  row.items.every((i) => i.text === row.items[0].text)
    ? [{ key: "all", brand: null, text: row.items[0].text }]
    : row.items.map((i) => ({ key: String(i.itemId), brand: i.brandName, text: i.text }));

export const isAskable = (row: Row) => row.items[0].party === "client" && row.items.some((i) => i.state === "todo");

export const toSegments = (item: OnboardingDetailItem): Array<{ state: ItemState; label: string }> => [
  { state: "todo", label: "To do" },
  ...(item.party === "us" ? [] : [{ state: "waiting" as const, label: item.party === "network" ? "Sent" : "Asked" }]),
  { state: "done", label: item.party === "network" ? "Live" : "Done" },
  { state: "not_needed", label: "Not needed" },
];

// Who did something: "You" for the signed-in user, else their first name, else the email the change was saved with.
export const toActorName = (email: string | null, firstName: string | null, me: string | undefined) =>
  (email && email.toLowerCase() === me?.toLowerCase() ? "You" : firstName || email || "Someone");

export function toHistoryText(entry: OnboardingHistoryEntry, item: OnboardingDetailItem, withBrand: boolean, me: string | undefined): string {
  const action = entry.action === "sent" && item.network ? `sent it to ${ITEM_NETWORK_LABELS[item.network]}` : HISTORY_ACTION_LABELS[entry.action];
  const brand = withBrand && item.brandName ? ` · ${item.brandName}` : "";
  return `${dayjs.utc(entry.createdAt).format("MMM D")} · ${toActorName(entry.actorName, entry.actorFirstName, me)} ${action}${brand}`;
}
